import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Manages the browser camera stream.
 *
 * Design choice: separated from the component so the stream lifecycle
 * can be managed independently and reused across views if needed.
 * Also makes unit testing easier (mock this service).
 */
@Injectable({ providedIn: 'root' })
export class CameraService implements OnDestroy {
  private stream: MediaStream | null = null;
  private _active$ = new BehaviorSubject<boolean>(false);

  /** Emits true while a camera stream is active. */
  readonly active$: Observable<boolean> = this._active$.asObservable();

  /**
   * Request the rear-facing camera at the desired resolution.
   * Falls back to any available camera if 'environment' is unavailable.
   */
  async start(constraints?: MediaTrackConstraints): Promise<MediaStream> {
    // Stop any previous stream first
    this.stop();

    const merged: MediaStreamConstraints = {
      video: {
        facingMode: 'environment',
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        ...constraints,
      },
    };

    try {
      this.stream = await navigator.mediaDevices.getUserMedia(merged);
    } catch {
      // Retry without facingMode constraint (desktop fallback)
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
      });
    }

    this._active$.next(true);
    return this.stream;
  }

  /** Stop all tracks and release the camera. */
  stop(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this._active$.next(false);
  }

  /** Get the current stream (may be null). */
  getStream(): MediaStream | null {
    return this.stream;
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
